import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { MailBankApi, CourierUserApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../../shared/api-config';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { updateUser } from '@state/authSlice';
import { useTrackEvent } from '../../utils/track';

export const usePhoneVerification = () => {
  const dispatch = useDispatch();
  const track = useTrackEvent();
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);

  const sendPhoneVerification = async (userId: string, method: 'sms' | 'whatsapp' = 'whatsapp', newPhone?: string) => {
    try {
      setIsSendingVerification(true);
      const mailbankApi = new MailBankApi(apiConfig());
      await mailbankApi.sendOtp({
        userId: userId,
        method: method,
        newContact: newPhone
      });
      showSuccessToast({
        title: 'Success',
        message: `Verification code sent via ${method === 'sms' ? 'SMS' : 'WhatsApp'}. Please check your messages.`,
      });
    } catch (error) {
      showErrorToast({
        title: 'Error',
        message: `Failed to send ${method === 'sms' ? 'SMS' : 'WhatsApp'} verification code`,
      });
    } finally {
      setIsSendingVerification(false);
    }
  };

  const verifyPhoneCode = async (code: string, userId: string, method: 'sms' | 'whatsapp' = 'whatsapp') => {
    try {
      setIsLoading(true);
      const courierUserApi = new CourierUserApi(apiConfig());
      const response = await courierUserApi.courierUserVerifyCode({
        code,
        userId: userId,
        method: method === 'sms' ? 'whatsapp' : method, // API might use 'whatsapp' for both
      });

      if (response.success) {
        // Fetch updated user data and update auth store so UI reflects verification immediately
        const updatedUser = await courierUserApi.courierUserFindById({ id: userId });
        dispatch(updateUser(updatedUser));
        track('phone_verified');

        showSuccessToast({
          title: 'Verified',
          message: 'Your phone number has been successfully verified.',
        });
        return { success: true };
      } else {
        showErrorToast({
          title: 'Verification Failed',
          message: response.message || 'Failed to verify phone number.',
        });
        return { success: false };
      }
    } catch (error) {
      console.error('Phone verification error:', error);
      showErrorToast({
        title: 'Error',
        message: 'Failed to verify phone number. Please try again.',
      });
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    isSendingVerification,
    sendPhoneVerification,
    verifyPhoneCode,
    // Keep old name for backwards compatibility
    verifyWhatsAppCode: verifyPhoneCode,
  };
};
